import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Home, Search, Pencil, Save, ArrowLeft, Calendar, Wallet, User, Filter, FileText, FileSpreadsheet, X } from 'lucide-react';
import { SortSelect, sortByField } from '../components/SortSelect';
import { FilterPanel } from '../components/FilterPanel';
import { ExportDialog } from '../components/ExportDialog';
import { useExport } from '../hooks/useExport';
import { exportTableToPDF } from '../utils/html2pdfExport';
import { tesisTuruText, tesisTuruMatches, tesisTuruOptionsFrom } from '../utils/tesisTuru';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTL = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || n === 0) return '-';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('tr-TR'); } catch { return iso; }
};

const daysRemaining = (iso) => {
  if (!iso) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(iso); end.setHours(0, 0, 0, 0);
  return Math.round((end - today) / 86400000);
};

const durumOf = (it) => {
  const d = daysRemaining(it.kira_bitis_tarihi);
  if (d === null) return 'belirsiz';
  if (d < 0) return 'suresi_gecen';
  if (d <= 30) return 'yaklasan';
  return 'aktif';
};

const DURUM_LABEL = {
  aktif: 'Aktif',
  yaklasan: '30 Gün İçinde Bitiyor',
  suresi_gecen: 'Süresi Geçen',
  belirsiz: 'Tarihi Belirtilmemiş',
};

const EXPORT_COLUMNS = [
  { key: 'sira', label: 'S.NO' },
  { key: 'tesis_adi', label: 'Tesis Adı' },
  { key: 'tasinmaz_turu', label: 'Tür' },
  { key: 'tesis_turu', label: 'Tesis Türü' },
  { key: 'il', label: 'İl' },
  { key: 'ilce', label: 'İlçe' },
  { key: 'mahalle', label: 'Mahalle' },
  { key: 'ada', label: 'Ada' },
  { key: 'parsel', label: 'Parsel' },
  { key: 'kiraya_verilen_kurum', label: 'Kiralayan / Kiraya Veren' },
  { key: 'kira_baslangic_tarihi', label: 'Kira Başlangıç' },
  { key: 'kira_bitis_tarihi', label: 'Kira Bitiş' },
  { key: 'gun_kalan', label: 'Kalan Gün' },
  { key: 'durum', label: 'Durum' },
  { key: 'kira_bedeli_kdv_dahil', label: 'Kira Bedeli (KDV Dahil)' },
  { key: 'aciklama', label: 'Açıklama' },
];

const SORT_DEFS = {
  bitis_yakin: { field: 'kira_bitis_tarihi', dir: 1, type: 'date' },
  bitis_uzak: { field: 'kira_bitis_tarihi', dir: -1, type: 'date' },
  bas_yeni: { field: 'kira_baslangic_tarihi', dir: -1, type: 'date' },
  bas_eski: { field: 'kira_baslangic_tarihi', dir: 1, type: 'date' },
  kira_yuksek: { field: 'kira_bedeli_kdv_dahil', dir: -1, type: 'number' },
  kira_dusuk: { field: 'kira_bedeli_kdv_dahil', dir: 1, type: 'number' },
  tesis_az: { field: 'tesis_adi', dir: 1 },
  tesis_za: { field: 'tesis_adi', dir: -1 },
  ilce_az: { field: 'ilce', dir: 1 },
};

const SORT_OPTIONS = [
  { value: 'bitis_yakin', label: 'Bitiş Tarihine En Yakın' },
  { value: 'bitis_uzak', label: 'Bitiş Tarihine En Uzak' },
  { value: 'bas_yeni', label: 'Başlangıç Tarihi (Yeni → Eski)' },
  { value: 'bas_eski', label: 'Başlangıç Tarihi (Eski → Yeni)' },
  { value: 'kira_yuksek', label: 'Kira Bedeli (Yüksek → Düşük)' },
  { value: 'kira_dusuk', label: 'Kira Bedeli (Düşük → Yüksek)' },
  { value: 'tesis_az', label: 'Tesis Adı (A→Z)' },
  { value: 'tesis_za', label: 'Tesis Adı (Z→A)' },
  { value: 'ilce_az', label: 'İlçe (A→Z)' },
];

const buildExportRow = (it, idx) => {
  const d = daysRemaining(it.kira_bitis_tarihi);
  return {
    sira: idx + 1,
    tesis_adi: it.tesis_adi || `${it.ilce || ''} ${it.mahalle ? '- ' + it.mahalle : ''}`.trim() || '-',
    tasinmaz_turu: it.tasinmaz_turu === 'arsa' ? 'Arsa' : it.tasinmaz_turu === 'yapi_yikildi' ? 'Yapı (YIKILDI)' : 'Yapı',
    tesis_turu: tesisTuruText(it.tesis_turu) || '-',
    il: it.il || 'İstanbul',
    ilce: it.ilce || '-',
    mahalle: it.mahalle || '-',
    ada: it.ada || '-',
    parsel: it.parsel || '-',
    kiraya_verilen_kurum: it.kiraya_verilen_kurum || '-',
    kira_baslangic_tarihi: formatDate(it.kira_baslangic_tarihi),
    kira_bitis_tarihi: formatDate(it.kira_bitis_tarihi),
    gun_kalan: d === null ? '-' : (d < 0 ? `${Math.abs(d)} gün geçti` : `${d} gün`),
    durum: DURUM_LABEL[durumOf(it)] || '-',
    kira_bedeli_kdv_dahil: formatTL(it.kira_bedeli_kdv_dahil),
    aciklama: it.aciklama || '-',
  };
};

const KiralikTasinmazIslemleriPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uiFilters, setUiFilters] = useState({
    search: '',
    il: [],
    ilce: [],
    tesis_turu: [],
    tasinmaz_turu: [],
    durum: [],
  });
  const [sortKey, setSortKey] = useState('bitis_yakin');
  const [showFilters, setShowFilters] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Export
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exporting, setExporting] = useState(false);
  const { selectedColumns, setSelectedColumns, pdfPageSize, setPdfPageSize, toggleColumn, selectAll, clearAll } = useExport('a4', EXPORT_COLUMNS.map(c => c.key));

  useEffect(() => {
    if (!user?.is_admin && !user?.has_emlak_kamulastirma_access) {
      navigate('/dashboard');
      return;
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/emlak-islemleri`, { withCredentials: true });
      const all = Array.isArray(res.data) ? res.data : (res.data.items || []);
      setItems(all.filter(e => e.tahsis_verildi_mi === 'kiralandi'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filtreleme + sıralama
  const filtered = useMemo(() => {
    let out = items;
    const s = uiFilters.search.trim().toLowerCase();
    if (s) {
      out = out.filter(e =>
        (e.tesis_adi || '').toLowerCase().includes(s) ||
        (e.ilce || '').toLowerCase().includes(s) ||
        (e.mahalle || '').toLowerCase().includes(s) ||
        (e.kiraya_verilen_kurum || '').toLowerCase().includes(s) ||
        (e.ada || '').toLowerCase().includes(s) ||
        (e.parsel || '').toLowerCase().includes(s) ||
        (e.aciklama || '').toLowerCase().includes(s)
      );
    }
    if (uiFilters.il.length > 0)          out = out.filter(e => uiFilters.il.includes(e.il || 'İstanbul'));
    if (uiFilters.ilce.length > 0)        out = out.filter(e => uiFilters.ilce.includes(e.ilce));
    if (uiFilters.tesis_turu.length > 0)  out = out.filter(e => tesisTuruMatches(e.tesis_turu, uiFilters.tesis_turu));
    if (uiFilters.tasinmaz_turu.length > 0) out = out.filter(e => uiFilters.tasinmaz_turu.includes(e.tasinmaz_turu));
    if (uiFilters.durum.length > 0)       out = out.filter(e => uiFilters.durum.includes(durumOf(e)));
    return sortByField(out, sortKey, SORT_DEFS);
  }, [items, uiFilters, sortKey]);

  // Dropdown seçenekleri (kayıtlardan türetilir)
  const filterOptions = useMemo(() => ({
    il:          [...new Set(items.map(e => e.il || 'İstanbul').filter(Boolean))].sort().map(v => ({ v, l: v })),
    ilce:        [...new Set(items.map(e => e.ilce).filter(Boolean))].sort().map(v => ({ v, l: v })),
    tesis_turu:  tesisTuruOptionsFrom(items),
    tasinmaz_turu: [{ v: 'arsa', l: 'Arsa' }, { v: 'yapi', l: 'Yapı' }, { v: 'yapi_yikildi', l: 'Yapı (YIKILDI)' }],
    durum: [
      { v: 'aktif', l: 'Aktif' },
      { v: 'yaklasan', l: '30 Gün İçinde Bitiyor' },
      { v: 'suresi_gecen', l: 'Süresi Geçen' },
      { v: 'belirsiz', l: 'Tarihi Belirtilmemiş' },
    ],
  }), [items]);

  const stats = useMemo(() => {
    let toplamKira = 0, yaklasanBitis = 0, biten = 0, aktif = 0;
    filtered.forEach(it => {
      toplamKira += parseFloat(it.kira_bedeli_kdv_dahil) || 0;
      const s = durumOf(it);
      if (s === 'suresi_gecen') biten++;
      else if (s === 'yaklasan') yaklasanBitis++;
      else if (s === 'aktif') aktif++;
    });
    return { toplamKayit: filtered.length, toplamKira, yaklasanBitis, biten, aktif };
  }, [filtered]);

  const openEdit = (it) => {
    setEditItem(it);
    setEditForm({
      kira_baslangic_tarihi: it.kira_baslangic_tarihi || '',
      kira_bitis_tarihi: it.kira_bitis_tarihi || '',
      kiraya_verilen_kurum: it.kiraya_verilen_kurum || '',
      kira_bedeli_kdv_dahil: it.kira_bedeli_kdv_dahil || '',
    });
  };

  const saveEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const payload = { ...editItem, ...editForm };
      delete payload._id;
      await axios.put(`${API}/emlak-islemleri/${editItem.id}`, payload, { withCredentials: true });
      setEditItem(null);
      fetchData();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const resetFilters = () => setUiFilters({ search: '', il: [], ilce: [], tesis_turu: [], tasinmaz_turu: [], durum: [] });

  const activeFilterCount = ['il', 'ilce', 'tesis_turu', 'tasinmaz_turu', 'durum']
    .reduce((c, k) => c + (uiFilters[k]?.length || 0), 0) + (uiFilters.search ? 1 : 0);

  const handleExport = async () => {
    if (selectedColumns.length === 0) { alert('En az bir sütun seçmelisiniz.'); return; }
    setExporting(true);
    try {
      const headers = selectedColumns.map(k => EXPORT_COLUMNS.find(c => c.key === k)?.label || k);
      const rows = filtered.map((it, i) => {
        const fullRow = buildExportRow(it, i);
        return selectedColumns.map(k => fullRow[k] ?? '-');
      });
      if (exportFormat === 'pdf') {
        await exportTableToPDF(headers, rows, `Kiralık Taşınmaz İşlemleri (${filtered.length} kayıt)`, `kiralik_tasinmaz_${Date.now()}.pdf`, { pageSize: pdfPageSize });
      } else {
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 2, 14) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Kiralık Taşınmazlar');
        XLSX.writeFile(wb, `kiralik_tasinmaz_${Date.now()}.xlsx`);
      }
      setExportDialogOpen(false);
    } catch (err) {
      alert('Export hatası: ' + err.message);
    } finally { setExporting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/emlak-kamulastirma')} variant="ghost" size="sm" className="text-gray-300 hover:text-white" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Geri
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">🔑 Kiralık Taşınmaz İşlemleri</h1>
                <p className="text-xs text-gray-400">Kurumumuzun kiraladığı (kiracısı olduğu) taşınmazlar · {items.length} toplam kayıt</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-300 hover:text-white">Dashboard</Button>
              <Button onClick={() => { logout(); navigate('/login'); }} variant="ghost" className="text-red-400 hover:text-red-300">Çıkış</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6" data-testid="kiralik-tasinmaz-page">
        {/* Özet İstatistikleri (mevcut filtre kapsamına göre) */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-purple-900/20 border-purple-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Home className="w-4 h-4 text-purple-300" /><span className="text-xs text-purple-300">Toplam (Filtre)</span></div>
              <p className="text-2xl font-bold text-white">{stats.toplamKayit}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-900/20 border-green-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Wallet className="w-4 h-4 text-green-300" /><span className="text-xs text-green-300">Toplam Aylık Kira</span></div>
              <p className="text-lg font-bold text-white">{formatTL(stats.toplamKira)}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-900/20 border-blue-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Calendar className="w-4 h-4 text-blue-300" /><span className="text-xs text-blue-300">Aktif</span></div>
              <p className="text-2xl font-bold text-white">{stats.aktif}</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-900/20 border-orange-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Calendar className="w-4 h-4 text-orange-300" /><span className="text-xs text-orange-300">30 Gün İçinde Bitiyor</span></div>
              <p className="text-2xl font-bold text-white">{stats.yaklasanBitis}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-900/20 border-red-700/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Calendar className="w-4 h-4 text-red-300" /><span className="text-xs text-red-300">Süresi Geçen</span></div>
              <p className="text-2xl font-bold text-white">{stats.biten}</p>
            </CardContent>
          </Card>
        </div>

        {/* Kontrol paneli: arama, filter toggle, sort, export */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={uiFilters.search}
              onChange={(e) => setUiFilters({ ...uiFilters, search: e.target.value })}
              placeholder="Tesis adı, ilçe, mahalle, kiralayan, ada/parsel..."
              className="pl-9 bg-gray-800 border-gray-700 text-white"
              data-testid="kiralik-search-input"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setShowFilters(v => !v)}
              variant="outline"
              className="border-gray-600 text-gray-200 hover:bg-gray-800"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" /> Filtreler {activeFilterCount > 0 && <Badge className="ml-2 bg-purple-600 text-white h-5 min-w-5 px-1.5">{activeFilterCount}</Badge>}
            </Button>
            <SortSelect value={sortKey} onChange={setSortKey} customOptions={SORT_OPTIONS} />
            <Button
              onClick={() => { setExportFormat('pdf'); setExportDialogOpen(true); }}
              disabled={filtered.length === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button
              onClick={() => { setExportFormat('excel'); setExportDialogOpen(true); }}
              disabled={filtered.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <FilterPanel
            filters={uiFilters}
            setFilters={setUiFilters}
            totalCount={items.length}
            filteredCount={filtered.length}
            resetFilters={resetFilters}
            filterConfig={[
              { key: 'tasinmaz_turu', label: 'Taşınmaz Türü',   type: 'multi', options: filterOptions.tasinmaz_turu },
              { key: 'durum',         label: 'Kira Durumu',      type: 'multi', options: filterOptions.durum },
              { key: 'il',            label: 'İl',              type: 'multi', options: filterOptions.il },
              { key: 'ilce',          label: 'İlçe',            type: 'multi', options: filterOptions.ilce },
              { key: 'tesis_turu',    label: 'Tesis Türü',      type: 'multi', options: filterOptions.tesis_turu },
            ]}
          />
        )}

        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>{activeFilterCount} filtre aktif · {filtered.length} sonuç ({items.length} toplam)</span>
            <button onClick={resetFilters} className="text-purple-400 hover:text-purple-300 inline-flex items-center gap-1" data-testid="reset-filters-btn">
              <X className="w-3 h-3" /> Temizle
            </button>
          </div>
        )}

        {/* Liste */}
        {loading ? (
          <div className="text-gray-400 text-center py-8">Yükleniyor...</div>
        ) : filtered.length === 0 ? (
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="py-12 text-center">
              <Home className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">
                {activeFilterCount > 0 ? 'Filtreye uyan kayıt bulunamadı.' : 'Henüz kiralık taşınmaz kaydı yok. Bir taşınmazın "Tahsis/Kiraya Verildi mi/Kiralandı Mı?" alanı için "Kiralandı" seçildiğinde burada görünecektir.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="kiralik-list">
            {filtered.map((it) => {
              const dRemaining = daysRemaining(it.kira_bitis_tarihi);
              const sevColor = dRemaining === null ? 'text-gray-400' : (dRemaining < 0 ? 'text-red-400 font-bold' : (dRemaining <= 30 ? 'text-orange-400 font-semibold' : 'text-gray-300'));
              const sevLabel = dRemaining === null ? '—' : (dRemaining < 0 ? `${Math.abs(dRemaining)} gün geçti` : (dRemaining === 0 ? 'Bugün son gün' : `${dRemaining} gün kaldı`));
              return (
                <Card key={it.id} className={`bg-gray-900/70 border ${dRemaining !== null && dRemaining < 30 ? 'border-orange-700/60' : 'border-gray-700/50'} hover:border-purple-600/50 transition-colors`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-purple-700/80 text-white text-[10px]">{it.tasinmaz_turu === 'arsa' ? 'ARSA' : it.tasinmaz_turu === 'yapi_yikildi' ? 'YAPI (YIKILDI)' : 'YAPI'}</Badge>
                          {tesisTuruText(it.tesis_turu) && <Badge className="bg-gray-700 text-white text-[10px]">{tesisTuruText(it.tesis_turu)}</Badge>}
                        </div>
                        <h3 className="text-base font-semibold text-white truncate" title={it.tesis_adi || `${it.ilce} - ${it.mahalle}`}>
                          {it.tesis_adi || `${it.ilce || ''} ${it.mahalle ? '— ' + it.mahalle : ''}`}
                        </h3>
                        <p className="text-xs text-gray-400 mt-1">
                          {it.ilce} {it.mahalle && `· ${it.mahalle}`}
                          {it.ada && it.parsel && ` · Ada ${it.ada} / Parsel ${it.parsel}`}
                        </p>
                      </div>
                      {!user?.is_viewer && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(it)} className="text-purple-300 hover:text-purple-200 h-8" data-testid={`edit-kiralik-${it.id}`}>
                          <Pencil className="w-4 h-4 mr-1" /> Düzenle
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3 text-gray-500" />
                        <span className="text-gray-500">Kiralayan/Kiraya Veren:</span>
                        <span className="text-white font-medium truncate">{it.kiraya_verilen_kurum || '-'}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-2 border-t border-gray-800">
                        <div>
                          <span className="text-gray-500">Kira Başlangıç:</span>
                          <p className="text-white font-medium">{formatDate(it.kira_baslangic_tarihi)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Kira Bitiş:</span>
                          <p className={sevColor}>{formatDate(it.kira_bitis_tarihi)}</p>
                          <p className={`text-[10px] ${sevColor}`}>{sevLabel}</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-800">
                        <span className="text-gray-500">Kira Bedeli (KDV Dahil):</span>
                        <p className="text-green-300 font-bold text-base">{formatTL(it.kira_bedeli_kdv_dahil)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Düzenleme Modal */}
      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="edit-kiralik-title">Kira Bilgilerini Düzenle</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-3 py-2">
              <p className="text-xs text-gray-400 pb-2 border-b border-gray-800">
                {editItem.tesis_adi || `${editItem.ilce} - ${editItem.mahalle}`}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kira Başlangıç Tarihi</Label>
                  <Input type="date" value={editForm.kira_baslangic_tarihi} onChange={(e) => setEditForm({ ...editForm, kira_baslangic_tarihi: e.target.value })} className="bg-gray-800 border-gray-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label>Kira Bitiş Tarihi</Label>
                  <Input type="date" value={editForm.kira_bitis_tarihi} onChange={(e) => setEditForm({ ...editForm, kira_bitis_tarihi: e.target.value })} className="bg-gray-800 border-gray-700 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Kiralayan / Kiraya Veren Kurum-Şahıs</Label>
                <Input value={editForm.kiraya_verilen_kurum} onChange={(e) => setEditForm({ ...editForm, kiraya_verilen_kurum: e.target.value })} className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="space-y-2">
                <Label>Kira Bedeli (KDV Dahil) (₺)</Label>
                <Input type="number" step="0.01" value={editForm.kira_bedeli_kdv_dahil} onChange={(e) => setEditForm({ ...editForm, kira_bedeli_kdv_dahil: e.target.value })} className="bg-gray-800 border-gray-700 text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditItem(null)} className="text-gray-400">Vazgeç</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-kiralik-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        columns={EXPORT_COLUMNS}
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
        onSelectAll={() => selectAll(EXPORT_COLUMNS)}
        onClearAll={clearAll}
        pdfPageSize={pdfPageSize}
        onPdfPageSizeChange={setPdfPageSize}
        onExportPDF={exportFormat === 'pdf' ? handleExport : undefined}
        onExportExcel={exportFormat === 'excel' ? handleExport : undefined}
        title={exportFormat === 'pdf' ? 'PDF İçin Sütun Seçin' : 'Excel İçin Sütun Seçin'}
        description={`${filtered.length} kayıt için sütunları seçin`}
        idPrefix={`kiralik-${exportFormat}`}
      />
    </div>
  );
};

export default KiralikTasinmazIslemleriPage;
